import type { SanityQuizBlock } from '@lib/sanity/types';

export interface LlmResult {
  name: string;
  description: string;
}

export interface QuizState {
  currentStepIndex: number;
  answers: Record<number, string>;
  isEmailCaptured: boolean;
  email: string;
  llmResult?: LlmResult;
}

export const INITIAL_QUIZ_STATE: QuizState = {
  currentStepIndex: 0,
  answers: {},
  isEmailCaptured: false,
  email: ''
};

export const countQuizSteps = (blocks: SanityQuizBlock[]): number =>
  blocks.filter((block) => block._type === 'quizStepBlock').length;

export const getQuizStepIndex = (
  blocks: SanityQuizBlock[],
  currentBlockIndex: number
): number => {
  let quizStepIndex = 0;

  for (let i = 0; i < currentBlockIndex; i++) {
    if (blocks[i]._type === 'quizStepBlock') {
      quizStepIndex++;
    }
  }

  return blocks[currentBlockIndex]?._type === 'quizStepBlock'
    ? quizStepIndex + 1
    : 0;
};

export const buildLlmPrompt = (
  template: string,
  answers: Record<number, string>
): string => {
  let prompt = template;

  for (const [stepNumber, answer] of Object.entries(answers)) {
    const placeholder = `{{answer_${stepNumber}}}`;
    prompt = prompt.replaceAll(placeholder, answer);
  }

  return prompt;
};

const MOCK_PREFIXES = [
  'Neon',
  'Cosmic',
  'Chaotic',
  'Stealth',
  'Turbo',
  'Mega',
  'Phantom',
  'Ultra',
  'Rogue',
  'Hyper',
  'Atomic',
  'Shadow'
];

const MOCK_SUFFIXES = [
  'xG Disaster',
  'Set Piece FC',
  'Wildcard Merchants',
  'Clean Sheet Chasers',
  'Differential Hunters',
  'Kneejerkers United',
  'Bench Boost Bandits',
  'Triple Captain Chaos',
  'Dead Team Walking'
];

const MOCK_DESCRIPTIONS = [
  'Aggressive. Unpredictable. Slightly delusional.',
  'Calculated chaos with a hint of overthinking.',
  'Bold moves, questionable captaincy choices.',
  'Lives for the differential. Dies by the differential.',
  'Calm on the outside, furious on the inside.',
  'Peak FPL energy. Zero chill.',
  'Tactical genius or total gambler. Hard to tell.',
  'One good gameweek away from being unbearable.'
];

const pickRandom = <T>(items: T[]): T =>
  items[Math.floor(Math.random() * items.length)];

const MOCK_LOADING_DELAY_MS = 4000;

export const generateMockResult = (
  answers: Record<number, string>
): Promise<LlmResult> => {
  const starSign = answers[1] ?? '';
  const capitalisedSign = starSign.charAt(0).toUpperCase() + starSign.slice(1);

  const name = `${pickRandom(MOCK_PREFIXES)} ${capitalisedSign} ${pickRandom(
    MOCK_SUFFIXES
  )}`;
  const description = pickRandom(MOCK_DESCRIPTIONS);

  return new Promise((resolve) => {
    setTimeout(() => resolve({ name, description }), MOCK_LOADING_DELAY_MS);
  });
};
